<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"

const FEE_NOTICE_CANCELED_REASON_MAX_LENGTH = 1000
const operationStore = useOperationStore()

const schema = yup.object().shape({
  feeNoticeCanceled: yup.boolean().required("Pflichtfeld"),

  feeNoticeCanceledReason: yup.string().when("feeNoticeCanceled", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  shipped: yup.boolean().when("feeNoticeCanceled", {
    is: false,
    then: (s) =>
      s.test(
        "is-checked",
        "Der Versand muss bestätigt werden",
        yupTestOnlyOnCreate((value) => value === true)
      ),
    otherwise: (s) => s.strip(),
  }),
  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

watch(
  () => state.feeNoticeCanceled,
  (value) => {
    if (value === false) {
      state.feeNoticeCanceledReason = undefined
    } else {
      state.shipped = undefined
    }
  }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Zwischengebührenbescheid"
      />
    </template>

    <IffContentSection
      headline="Zwischengebührenbescheid"
      :is-last-child="true"
    >
      <IffDualRadioGroup
        name="feeNoticeCanceled"
        v-model="state.feeNoticeCanceled"
        legend="Der Zwischengebührenbescheid entfällt"
        required
        aria-controls="fee-notice-canceled-reason-region shipped-region"
      />
      <div id="fee-notice-canceled-reason-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.feeNoticeCanceled === true"
            name="feeNoticeCanceledReason"
            label="Begründung"
            :help="`Noch ${
              FEE_NOTICE_CANCELED_REASON_MAX_LENGTH -
              (state.feeNoticeCanceledReason?.length ?? 0)
            } Zeichen verfügbar`"
            required
            class="max-w-[600px]"
          >
            <UTextarea
              v-model="state.feeNoticeCanceledReason"
              :maxlength="FEE_NOTICE_CANCELED_REASON_MAX_LENGTH"
              resize
              aria-required="true"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
      <div id="shipped-region" aria-live="polite">
        <IffTransitionHeight>
          <div v-if="state.feeNoticeCanceled === false">
            <UFormGroup
              v-if="state.feeNoticeCanceled === false"
              name="shipped"
              class="w-[600px]"
            >
              <UCheckbox
                label="Der Versand der erforderlichen Dokumente an die antragstellende Person ist erfolgt"
                v-model="state.shipped"
                required
                aria-required="true"
              />
            </UFormGroup>
            <div id="shipping-confirmation-date-region" aria-live="polite">
              <IffTransitionHeight>
                <UFormGroup
                  name="shippingConfirmationDate"
                  label="Datum Zwischengebührenbescheid"
                  v-if="state.shipped"
                  required
                  class="max-w-[300px] w-fit"
                >
                  <template #hint>
                    <UButton
                      @click="
                        state.shippingConfirmationDate = new Date()
                          .toISOString()
                          .slice(0, 10)
                      "
                      icon="i-ic-outline-access-time"
                      size="sm"
                      color="gray"
                      variant="ghost"
                      :trailing="false"
                      aria-label="Aktuelles Datum wählen"
                      title="Aktuelles Datum wählen"
                    />
                  </template>

                  <UInput
                    v-model="state.shippingConfirmationDate"
                    type="date"
                    aria-required="true"
                    class="whitespace-normal max-w-[150px]"
                  />
                </UFormGroup>
              </IffTransitionHeight>
            </div>
          </div>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
